/**
 * Recuperarea parolei.
 *
 * Ambele ecrane respectă regula anti-enumerare: indiferent dacă emailul există sau nu,
 * mesajul afișat este identic, deci nimeni nu poate afla ce conturi există.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AppShell.js';
import { Button, Field, InlineAlert, Input } from '../components/ui.js';
import { api } from '../lib/api.js';
import { errorMessage, fieldErrors } from '../lib/session.js';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/auth/password/forgot', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <h1 className="font-display text-display-m text-ink-950">Recuperarea parolei</h1>
        <p className="mt-2 mb-6 text-body-s text-ink-600">
          Îți trimitem un link valabil 60 de minute. Din motive de securitate, schimbarea parolei
          închide toate sesiunile active.
        </p>

        {sent ? (
          <InlineAlert tone="success" title="Verifică-ți emailul">
            Dacă adresa există în platformă, ai primit un link de resetare. Verifică și folderul de spam.
          </InlineAlert>
        ) : (
          <>
            {error ? (
              <div className="mb-4">
                <InlineAlert tone="danger">{error}</InlineAlert>
              </div>
            ) : null}
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <Field label="Email" required>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nume@restaurant.ro"
                  />
                )}
              </Field>
              <Button type="submit" size="lg" loading={loading} className="w-full">
                Trimite linkul
              </Button>
            </form>
          </>
        )}

        <p className="mt-6 text-body-s text-ink-600">
          <Link to="/login" className="transition-ui font-medium text-brand-600 hover:text-brand-700">
            Înapoi la autentificare
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <AuthLayout>
        <div className="w-full max-w-sm">
          <InlineAlert tone="warning" title="Link incomplet">
            Linkul de resetare nu conține tokenul. Cere un link nou din pagina de recuperare.
          </InlineAlert>
          <p className="mt-4 text-body-s">
            <Link to="/forgot-password" className="font-medium text-brand-600">
              Cere un link nou
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const nextErrors: Record<string, string[]> = {};
    if (password.length < 10) nextErrors.password = ['Parola trebuie să aibă minim 10 caractere.'];
    if (password !== confirm) nextErrors.confirm = ['Parolele nu coincid.'];
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    try {
      await api.post('/auth/password/reset', { token, password });
      setDone(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <h1 className="font-display text-display-m text-ink-950">Parolă nouă</h1>
        <p className="mt-2 mb-6 text-body-s text-ink-600">Alege o parolă pe care nu ai mai folosit-o.</p>

        {done ? (
          <InlineAlert tone="success" title="Parola a fost schimbată">
            Te redirecționăm către autentificare…
          </InlineAlert>
        ) : (
          <>
            {error ? (
              <div className="mb-4">
                <InlineAlert tone="danger">{error}</InlineAlert>
              </div>
            ) : null}
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <Field label="Parolă nouă" required errors={errors.password} hint="Minim 10 caractere.">
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Confirmă parola" required errors={errors.confirm}>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" size="lg" loading={loading} className="w-full">
                Salvează parola
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
